import React from 'react';
import { ChatEventType, PossibleEnrichments, Vendor } from '@outdoorly/react-chat-sdk';
import { useMemo } from 'react';
import type { Message } from '@outdoorly/react-chat-sdk';
import { ChatMessage } from '../Message/ChatMessage';
import { Avatar, Box, Flex, Text } from '@chakra-ui/react';
import { EmojiAndLinkifiedText } from '../Message/EmojiAndLinkifiedText';
import { SkeletonChatImage } from '../SkeletonImage';
import { MessageAttributes } from '.';
import { tryToBuildImgixUrl } from '../../utils/imgix';
import dayjs from 'dayjs';
import { MessageEvent, useEvents } from '../Message/MessageEvent';
import { MotionBox } from '../Motion';
import { Store } from '../../store';

const findLastCustomerMessage = (filteredMessages: Message[], joinIndex: number, sid: string) => {
  //loop backwards from join event to find the most recent customer message for quote block
  for (let index = joinIndex - 1; index >= 0; index--) {
    const messageAttributes = filteredMessages[index]?.attributes as MessageAttributes;
    if (
      filteredMessages[index]?.author !== 'system' &&
      !Boolean(messageAttributes?.expert) &&
      filteredMessages[index]?.body
    ) {
      Store.update((s) => {
        s.expertMatchedMessage[sid] = filteredMessages[index].body as string;
      });
      return;
    }
  }
};

const wasAIExpertLastToLeaveConversation = (filteredMessages: Message[], joinIndex: number, sid: string) => {
  //loop backwards from join event to find the most recent leave or join event,
  //if AI left last, it's an AI transfer.
  const interruptingEvents = new Set<ChatEventType>([
    ChatEventType.Joined,
    ChatEventType.Left,
    ChatEventType.Closed,
    ChatEventType.Reopened,
    ChatEventType.Started
  ]);
  for (let index = joinIndex - 1; index >= 0; index--) {
    const messageAttributes = filteredMessages[index]?.attributes as MessageAttributes;
    if (messageAttributes?.type && interruptingEvents.has(messageAttributes?.type)) {
      return;
    }
    if (messageAttributes?.type === ChatEventType.AiLeft) {
      Store.update((s) => {
        s.AITransferExpertJoined[sid] = true;
      });
      return;
    }
  }
};

export const useMessageGroups = (messages: Message[]) => {
  const events = useEvents({ labelSubstitutions: {} });
  const filteredMessages = useMemo(
    () =>
      messages?.filter((message) => {
        const messageAttributes = message.attributes as MessageAttributes;
        return (
          messageAttributes?.type === 'Message' || Object.keys(events[messageAttributes.type] ?? {}).length
        );
      }),
    [messages] // the keys of events will stay static
  );
  const expertJoinRecord = Store.useState((s) => s.expertMatchedMessage);
  const AITransferExpertJoined = Store.useState((s) => s.AITransferExpertJoined);
  const groupedMessages = useMemo(
    () =>
      filteredMessages.reduce(
        (acc: Message[][], message: Message, index, allMessages) => {
          // Push the current message onto the group
          acc.at(-1)?.push(message);

          const attributes = message.attributes as MessageAttributes;
          const nextMessage = allMessages[index + 1];
          if (
            (attributes?.type === ChatEventType.Joined || attributes?.type === ChatEventType.AiJoined) &&
            !expertJoinRecord[message.sid]
          ) {
            findLastCustomerMessage(allMessages, index, message.sid);
          }
          if (attributes?.type === ChatEventType.Joined && !AITransferExpertJoined[message.sid]) {
            wasAIExpertLastToLeaveConversation(allMessages, index, message.sid);
          }
          // If the next message will be from a different author
          // or our most recent message was an event (recommendation, etc.)
          // then we terminate the current group and begin another
          // (unless this was the last message)
          if (
            (nextMessage?.author !== message.author ||
              (attributes.type !== ChatEventType.Message && attributes.type !== ChatEventType.Recommended)) &&
            index < allMessages.length - 1
          ) {
            acc.push([]);
          }
          return acc;
        },
        [[]] as Message[][]
      ),
    [messages]
  );
  return groupedMessages;
};

export const MessageGroup = ({
  messageGroup,
  vendor,
  isLastGroup,
  nextGroupIsSameAuthor,
  eventIsPiiCollectionFlow,
  scrollToBottomIfClose
}: {
  messageGroup: Message[];
  vendor?: Vendor;
  scrollToBottomIfClose: () => void;
  isLastGroup: boolean;
  nextGroupIsSameAuthor: boolean;
  eventIsPiiCollectionFlow: boolean;
}) => {
  const firstMessage = messageGroup.at(0);
  const lastMessage = messageGroup.at(-1)!;
  const attributes = firstMessage?.attributes as MessageAttributes;
  const isExpert =
    (firstMessage?.author !== 'system' && !!attributes.expert?.expertProfile) ||
    !!attributes.impersonatedExpert;

  const isEventOnly = attributes.type !== ChatEventType.Message && firstMessage === lastMessage;
  const isLastMessageAnEvent = (lastMessage.attributes as MessageAttributes).type !== ChatEventType.Message;

  const avatarSource =
    attributes.expert?.expertProfile?.avatar ?? attributes.impersonatedExpert?.avatar ?? vendor?.logo;
  const avatarUrl = avatarSource
    ? tryToBuildImgixUrl(avatarSource, {
        w: 50,
        h: 50,
        auto: 'format,compress',
        crop: 'faces',
        fit: 'crop'
      })
    : undefined;

  const lastAttributes = lastMessage.attributes as MessageAttributes;
  const lastEmail = useMemoizedEmail(lastAttributes);
  return (
    <Box key={firstMessage?.sid}>
      {!isEventOnly && (
        <Flex pos="relative" w="100%" key={firstMessage?.sid}>
          <MotionBox
            pos="absolute"
            initial={{ bottom: '0px', opacity: 0 }}
            animate={{ bottom: '20px', opacity: 1 }}
            key="avatar"
          >
            {isExpert && !isLastMessageAnEvent && (
              <Avatar
                src={avatarUrl}
                w="24px"
                h="24px"
                ignoreFallback
                role="presentation"
                aria-hidden={true}
              />
            )}
          </MotionBox>
          <Flex w="100%" flexDir="column" key="messageGroup">
            {(isLastMessageAnEvent ? messageGroup.slice(0, -1) : messageGroup).map((m, index, array) => (
              <MessageContainer
                key={m.sid}
                message={m}
                expertProfile={(m.attributes as PossibleEnrichments)?.expert?.expertProfile}
                isLastMessageInGroup={index === messageGroup.length - 1}
                scrollToBottomIfClose={scrollToBottomIfClose}
                isLastMessage={isLastGroup && index === messageGroup.length - 1}
              />
            ))}
          </Flex>
        </Flex>
      )}
      {isLastMessageAnEvent && (
        <MessageEvent
          id={lastMessage.sid}
          email={lastEmail}
          eventType={lastAttributes.type}
          expert={lastAttributes.expert}
          impersonatedExpert={lastAttributes.impersonatedExpert}
          recommendation={lastAttributes.recommendation}
          isLastMessageInGroup={!nextGroupIsSameAuthor}
          isLastMessage={isLastGroup}
          dateUpdated={dayjs(lastMessage.dateUpdated).toString()}
          isPiiCollectionFlow={eventIsPiiCollectionFlow}
          npsRequestId={lastAttributes.npsRequestId ?? undefined}
          npsRequest={lastAttributes.npsRequest}
          cancelled={lastAttributes.cancelled}
          idleCloseTime={lastAttributes.idleCloseTime}
          scrollToBottomIfClose={scrollToBottomIfClose}
        />
      )}
    </Box>
  );
};

export const MessageContainer = ({
  message,
  isLastMessage,
  isLastMessageInGroup,
  expertProfile,
  scrollToBottomIfClose
}: {
  message: Message;
  isLastMessageInGroup: boolean;
  isLastMessage: boolean;
  expertProfile: NonNullable<PossibleEnrichments['expert']>['expertProfile'];
  scrollToBottomIfClose: () => void;
}) => {
  const attributes = message.attributes as MessageAttributes;
  const isExpertOrSystem = message?.author === 'system' || Boolean(attributes.expert);
  const isExpert = message.author !== 'system' && !!expertProfile;
  const isAiGenerated = Boolean(attributes.impersonatedExpert);
  const email = useMemoizedEmail(attributes);

  const body = useMemo(
    () =>
      message?.body ? (
        <Text
          variant="paragraph-medium"
          color={isExpertOrSystem ? 'scheme.contentPrimary' : 'scheme.contentInversePrimary'}
        >
          <EmojiAndLinkifiedText body={message.body} id={message?.sid ?? ''} />
        </Text>
      ) : message?.attachedMedia && message?.attachedMedia.length > 0 ? (
        <SkeletonChatImage media={message.attachedMedia[0]} scrollToBottomIfClose={scrollToBottomIfClose} />
      ) : (
        <></>
      ),
    [message]
  );
  //allow recommendations to be grouped with other messages from the expert
  if (attributes.type === ChatEventType.Recommended) {
    return (
      <MessageEvent
        id={message.sid}
        email={email}
        eventType={attributes.type}
        expert={attributes.expert}
        impersonatedExpert={attributes.impersonatedExpert}
        recommendation={attributes.recommendation}
        isLastMessageInGroup={isLastMessageInGroup}
        isLastMessage={isLastMessage}
        dateUpdated={dayjs(message.dateUpdated).toString()}
        isPiiCollectionFlow={false}
        npsRequestId={attributes.npsRequestId ?? undefined}
        npsRequest={attributes.npsRequest}
        cancelled={attributes.cancelled}
        idleCloseTime={attributes.idleCloseTime}
        scrollToBottomIfClose={scrollToBottomIfClose}
      />
    );
  }
  return (
    <ChatMessage
      isExpertOrSystem={isExpertOrSystem}
      isAiGenerated={isAiGenerated}
      isLastMessageInGroup={isLastMessageInGroup}
      id={message.sid}
      expertName={attributes.expert?.expertProfile?.publicName}
      dateUpdated={dayjs(message.dateUpdated).toString()}
      isLastMessage={isLastMessage}
      scrollToBottomIfClose={scrollToBottomIfClose}
    >
      {body}
    </ChatMessage>
  );
};

const useMemoizedEmail = (attributes: MessageAttributes) => {
  return useMemo(
    () => ({ censored: attributes.censored, completed: attributes.completed, skipped: attributes.skipped }),
    [attributes.censored, attributes.completed, attributes.skipped]
  );
};
